<template>
  <v-dialog v-model="dialog" :width="dialogWidth" :scrollable="true">
    <v-card>
      <v-card-title class="headline">
        <span>{{ $t('matchdialog.title') }}</span>
      </v-card-title>
      <v-card-text>
        <v-container class="pa-0">
          <v-row>
            <v-col cols="12">
              <schedule-match
                  :uid="uid"
                  :result="editable ? null : result"
                  :initialGoals="result ? result.goals : null"
                  :initialOpponentGoals="result ? result.opponent_goals : null"
                  :player="player"
                  :opponent="opponent"
                  :game="game"
                  :single-games="result ? result.singleGames : null"
                  :allowDetailsDialog="false"
                  :editable="!!(editable && canEnterResults)"
                  v-on:matchStored="close()"
                  :chat-open="!!chatChannelName"
                  @openChat="initChat"
              />
            </v-col>
            <v-col cols="12" v-if="result && result.confirmedAt" class="pt-0 pb-0">
              <v-col>
                <v-icon left class="resultConfirmedIcon">mdi-check-circle</v-icon>
                {{ $t('matchdialog.confirmedAt', { confirmedAt: $datefns.formatTimeRelative(result.confirmedAt), confirmedBy: result.confirmedBy, updatedBy: result.updatedBy }) }}
              </v-col>
            </v-col>
            <v-col cols="12" v-if="confirmResultPossible">
              <v-btn class="" :disabled="loading || !isLoggedIn" @click="confirmResult()">
                <v-icon left>mdi-check-circle</v-icon>
                {{ $t('matchdialog.confirm') }}
              </v-btn>
            </v-col>
            <v-col cols="12" v-if="vetoPossible">
              <v-btn class="" :disabled="loading || !isLoggedIn" @click="openVetoDialog()">
                <v-icon left>mdi-bugle</v-icon>
                {{ $t('matchdialog.veto') }}
              </v-btn>
              <veto-dialog v-if="this.showVetoDialog" :visible="this.showVetoDialog" :matchUid="uid"
                           :closeAction="closeVetoDialog" />
            </v-col>
          </v-row>
          <v-row v-if="canUploadEvidenceImage">
            <v-col cols="12">
              <v-btn id="upload-evidence-image" class="secondary">
                <v-icon left>mdi-camera</v-icon>
                {{ $t('matchdialog.upload-evidence-image') }}
              </v-btn>
            </v-col>
            <image-upload-with-preview :triggerId="'#upload-evidence-image'"
                                       :uploadAction="getUploadEvidenceImagePromise" />
          </v-row>
          <div v-if="evidenceImages && evidenceImages.length > 0">
            <v-row no-gutters>
              <v-col cols="12">
                <span class="subtitle-1">{{ $t('matchdialog.evidence-images') }}</span>
              </v-col>
            </v-row>
            <v-row v-for="(img) in evidenceImages" :key="img.uid" class="align-center">
              <v-col cols="2" @click="openEvidenceImageDialog(img)">
                <v-btn class="secondary" icon>
                  <v-icon>mdi-image</v-icon>
                </v-btn>
              </v-col>
              <v-col cols="8" @click="openEvidenceImageDialog(img)">
                <span>Beweisbild von {{ img.user.name }}</span><br />
                <span class="timestamp">{{ (new Date(img.timestamp * 1000)).toLocaleString() }}</span>
              </v-col>
              <v-col cols="2">
                <v-btn v-if="game.youAreAdmin || img.user.uid == $store.state.profile.uid" class="" icon color="error"
                       @click="deleteEvidenceImage(img)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
                <confirm-dialog
                    v-if="evidenceImageToDelete"
                    :visible="evidenceImageToDelete != undefined"
                    :title="'Beweisbild löschen'"
                    :entity="evidenceImageToDelete"
                    :text="'Willst du das Beweisbild wirklich löschen?'"
                    confirmButtonClasses="error"
                    :closeAction="function() {evidenceImageToDelete = undefined;}"
                    :confirmAction="confirmedImageDeletion"
                ></confirm-dialog>
              </v-col>
            </v-row>
            <image-dialog
                v-if="openEvidenceImage"
                :url="openEvidenceImage.url"
                :visible="true"
                :closeAction="function() {openEvidenceImage=undefined}"
            ></image-dialog>
          </div>
          <div v-if="canNominatePlayer">
            <p class="subtitle-1">Termin</p>
            <v-row>
              <v-col cols="10">
                <date-time-picker v-model="startsAt" :label="$t('competition.startDate')" :disabled="loading" />
              </v-col>
              <v-col cols="2">
                <v-btn color="primary" block height="54" @click="addDates">
                  <v-icon>mdi-content-save</v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <p class="caption mb-0 grey--text">Optional</p>
            <v-row>
              <v-col cols="10">
                <date-time-picker v-model="endsAt" :label="$t('competition.endDate')" :disabled="loading" />
              </v-col>
              <v-col cols="2">
                <v-btn color="primary" block height="54" @click="addDates">
                  <v-icon>mdi-content-save</v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <p class="caption mb-0 grey--text">Optional</p>
            <v-row>
              <v-col cols="10">
                <v-text-field v-model="dayHint" :label="$t('competition.dayHint')" :disabled="loading" />
              </v-col>
              <v-col cols="2">
                <v-btn color="primary" block height="54" @click="addDates">
                  <v-icon>mdi-content-save</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </div>
          <v-row>
            <v-col cols="12">
              <span class="subtitle-1">{{ $t('matchdialog.participants') }}</span>
            </v-col>
            <v-col cols="12">
              <team-name v-if="player.type == 'team'" :team="player" />
              <div v-else>
                <user-widget :user="player" :game="game" :showDefaultActions="['details']"
                             :renderSocialiteIcons="false" />
                <user-online-id class="ml-2 pb-2" style="font-size:120%;" v-if="playerOnlineId" :highlight="true"
                                :onlineId="playerOnlineId.onlineId" :platformCode="playerOnlineId.platformCode" />
                <socialite-open-button class="ml-2" v-if="twitchAccount(player)"
                                       :externalAccount="twitchAccount(player)" />
                <socialite-open-button class="ml-2" v-if="youTubeAccount(player)"
                                       :externalAccount="youTubeAccount(player)" />
              </div>
              <div v-if="game.mode === 'vbl' && canNominatePlayer">
                <v-row>
                  <v-col cols="10">
                    <v-select
                        v-model="playerNomination"
                        label="Nominierung"
                        :items="player.teamPlayers"
                        item-text="player.name"
                        item-value="player.uid"
                        hide-details="auto"
                        dense
                        filled
                    ></v-select>
                  </v-col>
                  <v-col cols="2">
                    <v-btn color="primary" block height="54" @click="nominatePlayer(playerNomination, player)">
                      <v-icon>mdi-content-save</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </div>
            </v-col>
            <v-col cols="12">
              <team-name v-if="opponent.type == 'team'" :team="opponent" />
              <div v-else>
                <user-widget :user="opponent" :game="game" :showDefaultActions="['details']"
                             :renderSocialiteIcons="false" />
                <user-online-id class="ml-2 pb-2" style="font-size:120%;" v-if="opponentOnlineId" :highlight="true"
                                :onlineId="opponentOnlineId.onlineId" :platformCode="opponentOnlineId.platformCode" />
                <socialite-open-button class="ml-2" v-if="twitchAccount(opponent)"
                                       :externalAccount="twitchAccount(opponent)" />
                <socialite-open-button class="ml-2" v-if="youTubeAccount(opponent)"
                                       :externalAccount="youTubeAccount(opponent)" />
              </div>
              <div v-if="game.mode === 'vbl' && canNominateOpponent">
                <v-row>
                  <v-col cols="10">
                    <v-select
                        v-model="opponentNomination"
                        label="Nominierung"
                        :items="opponent.teamPlayers"
                        item-text="player.name"
                        item-value="player.uid"
                        hide-details="auto"
                        dense
                        filled
                    ></v-select>
                  </v-col>
                  <v-col cols="2">
                    <v-btn color="primary" block height="54" @click="nominatePlayer(opponentNomination, opponent)">
                      <v-icon>mdi-content-save</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>

              </div>
            </v-col>
          </v-row>
        </v-container>
        <v-btn v-if="!chatChannelName" color="primary" @click="initChat">Chat öffnen</v-btn>
      </v-card-text>
      <v-divider></v-divider>
      <chat
          v-if="chatChannelName"
          :other-channel-name="chatChannelName"
          :game="game"
          :height="150"
      />
      <v-card-actions>
        <v-spacer />
        <v-btn class="primary" text @click="close()">
          <v-icon left>mdi-close</v-icon>
          {{ $t('common.close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  EVENT_GAME_RESULT_AVAILABLE,
  EVENT_REFRESH_DATA
} from '@/plugins/eventbus'
import ImageUploadWithPreview from '@/widgets/ImageUploadWithPreview'
import ImageDialog from '@/widgets/ImageDialog'
import ScheduleMatch from '@/widgets/ScheduleMatch'
import ConfirmDialog from '@/widgets/ConfirmDialog'
import UserOnlineId from '@/widgets/UserOnlineId'
import VetoDialog from '@/widgets/VetoDialog'
import SocialiteOpenButton from './SocialiteOpenButton.vue'
import TeamName from './TeamName.vue'
import Chat from '@/widgets/Chat'
import DateTimePicker from './DateTimePicker'

export default {
  name: 'GameMatchDialog',
  components: {
    DateTimePicker,
    ImageUploadWithPreview,
    ImageDialog,
    ScheduleMatch,
    ConfirmDialog,
    UserOnlineId,
    VetoDialog,
    SocialiteOpenButton,
    TeamName,
    Chat
  },
  props: {
    uid: {
      type: String,
      required: true
    },
    player: {
      type: Object,
      required: true
    },
    opponent: {
      type: Object,
      required: true
    },
    chatChannel: {
      type: String,
      required: false
    },
    result: {
      type: Object,
      required: false
    },
    game: {
      type: Object,
      required: true
    },
    editable: {
      type: Boolean,
      required: false,
      default: false
    },
    visible: {
      type: Boolean,
      required: true
    },
    closeAction: {
      type: Function,
      required: true
    },
    matchDate: {
      type: Object,
      required: false
    }
  },
  data: function () {
    return {
      dialog: false,
      loading: false,
      evidenceImages: undefined,
      openEvidenceImage: undefined,
      evidenceImageToDelete: undefined,
      showVetoDialog: false,
      playerNomination: null,
      opponentNomination: null,
      nominationCounter: 0,
      chatChannelName: '',
      startsAt: null,
      endsAt: null,
      dayHint: '',
    }
  },
  computed: {
    canEnterResults() {
      if (this.game.mode === 'vbl') {
        let initialNominationCounter = 0
        if (this.player?.nominatedPlayer) {
          initialNominationCounter++
        }
        if (this.opponent?.nominatedPlayer) {
          initialNominationCounter++
        }

        return !!((this.playerNomination && this.opponentNomination) && (initialNominationCounter + this.nominationCounter === 2))
      }

      return true
    },
    dialogWidth: function () {
      if (this.$vuetify.breakpoint.smAndUp) {
        return '450'
      }
      return '90%'
    },
    canUploadEvidenceImage: function () {
      if (this.isWildcardMatch) {
        return false
      }
      return this.isAdminOrAnyPlayer
    },
    isLoggedIn() {
      return this.$store.state.isLoggedIn
    },
    canNominatePlayer() {
      if (this.game.youAreAdmin) {
        return true
      }
      if (this.player?.type === 'team') {
        let playerUid = this.$store.state.profile ? this.$store.state.profile.uid : null
        return this.player?.teamPlayers?.filter(tp => tp.player.uid === playerUid)?.length > 0
      }
      return false
    },
    canNominateOpponent() {
      if (this.game.youAreAdmin) {
        return true
      }
      if (this.opponent?.type === 'team') {
        let playerUid = this.$store.state.profile ? this.$store.state.profile.uid : null
        return this.opponent?.teamPlayers?.filter(tp => tp.player.uid === playerUid)?.length > 0
      }
      return false
    },
    isAdminOrAnyPlayer: function () {
      let playerUid = this.$store.state.profile ? this.$store.state.profile.uid : null
      return this.game.youAreAdmin || (this.player.uid == playerUid || this.opponent.uid == playerUid)
    },
    isWildcardMatch: function () {
      return this.opponent.name == '*' || this.player.name == '*'
    },
    vetoPossible: function () {
      if (this.isWildcardMatch || !this.result || (this.result && this.result.confirmedAt)) {
        return false
      }
      return true
    },
    confirmResultPossible: function () {
      if (this.isWildcardMatch) {
        return false
      }
      if (this.result && !this.result.confirmedAt) {
        return true
      }
      return false
    },
    playerOnlineId: function () {
      return this.player.onlineIds ? this.player.onlineIds[this.game.platform] : undefined
    },
    opponentOnlineId: function () {
      return this.opponent.onlineIds ? this.opponent.onlineIds[this.game.platform] : undefined
    }
  },
  watch: {
    visible: function (val) {
      this.dialog = val
    },
    dialog: function (val) {
      if (val === false) {
        this.closeAction()
      }
    },
    value: function () {
      // console.log("value changed to", val);
    }
  },
  created() {},
  mounted() {
    this.dialog = this.visible
    if (!this.isWildcardMatch) {
      this.refresh()
    }
    this.chatChannelName = this.chatChannel
    this.playerNomination = this.player?.nominatedPlayer?.uid ?? null
    this.opponentNomination = this.opponent?.nominatedPlayer?.uid ?? null
    this.startsAt = this.matchDate?.starts_at ? new Date(this.matchDate.starts_at) : null
    this.endsAt = this.matchDate?.ends_at ? new Date(this.matchDate.ends_at) : null
    this.dayHint = this.matchDate?.day_hint ? this.matchDate.day_hint : null
  },
  methods: {
    initChat() {
      this.loading = true
      this.$api
          .initChatForMatch(this.game.uid, this.uid, {})
          .then((res) => {
            this.loading = false
            this.chatChannelName = res.data.chatChannel
            this.$eventBus.$emit(EVENT_REFRESH_DATA)
          })
          .catch(error => {
            this.loading = false
            this.$common.onError(error)
          })
    },
    nominatePlayer(player, team) {
      const payload = {
        userUid: player,
        teamUid: team.uid,
        matchUid: this.uid
      }
      this.$api
          .addNomination(this.game.uid, payload)
          .then(() => {
            this.$eventBus.$emit(EVENT_REFRESH_DATA)
            this.loading = false
            this.nominationCounter++
          })
          .catch(error => {
            this.loading = false
            this.$common.onError(error)
          })
    },
    addDates() {
      const payload = {
        matchUid: this.uid,
      }
      if (this.startsAt) {
        payload.startsAt = this.startsAt
      }
      if (this.endsAt) {
        payload.endsAt = this.endsAt
      }
      if (this.dayHint) {
        payload.dayHint = this.dayHint
      }
      this.$api
          .addDates(this.game.uid, payload)
          .then(() => {
            this.$eventBus.$emit(EVENT_REFRESH_DATA)
            this.loading = false
          })
          .catch(error => {
            this.loading = false
            this.$common.onError(error)
          })
    },
    youTubeAccount: function (player) {
      return this.getExternalAccount(player, 'youtube')
    },
    twitchAccount: function (player) {
      return this.getExternalAccount(player, 'twitch')
    },
    getExternalAccount(player, driver) {
      if (!player.externalAccounts) {
        return undefined
      }
      return player.externalAccounts.find(function (ext) {
        return ext.driver == driver
      })
    },
    refresh() {
      if (!this.isLoggedIn) {
        return
      }
      this.$api.getEvidenceImages(this.game.uid, this.uid)
          .then(response => {
            let data = response.data
            this.evidenceImages = data.list
          })
          .catch(error => {
            this.$common.onError(error)
          })
    },
    close() {
      this.dialog = false
    },
    getUploadEvidenceImagePromise(formData) {
      return this.$api.uploadEvidenceImage(this.game.uid, this.uid, formData)
          .then(response => {
            let data = response.data
            this.evidenceImages = data.list
          })
    },
    openEvidenceImageDialog(img) {
      this.openEvidenceImage = img
    },
    deleteEvidenceImage(img) {
      this.evidenceImageToDelete = img
      console.log('delete? ' + this.evidenceImageToDelete.uid)
    },
    confirmResult() {
      this.loading = true
      this.$api.confirmMatchResult(this.game.uid, this.uid)
          .then(response => {
            let data = response.data
            this.$eventBus.$emit(EVENT_GAME_RESULT_AVAILABLE, {
              gameuid: this.game.uid,
              matchUid: this.uid,
              result: data.result
            })
            this.$eventBus.$emit(EVENT_REFRESH_DATA)
            this.loading = false
          })
          .catch(error => {
            this.loading = false
            this.$common.onError(error)
          })
    },
    confirmedImageDeletion(img) {
      console.log('deleting ' + img.uid)
      this.loading = true
      this.$api.deleteEvidenceImage(this.game.uid, this.uid, img.uid)
          .then(() => {
            this.loading = false
            this.evidenceImages = this.evidenceImages.filter((img2) => {
              return img2.uid != img.uid
            })
          })
          .catch(error => {
            this.loading = false
            this.$common.onError(error)
          })
    },
    openVetoDialog() {
      this.showVetoDialog = true
    },
    closeVetoDialog() {
      this.showVetoDialog = false
    }
  }
}
</script>
<style scoped>
.timestamp {
  color: #888;
}

.resultConfirmedIcon {
  color: var(--v-primary-base);
}
</style>
